/**
 * The due queue (SPEC §2 `scheduler/queue.ts`, §6.2).
 *
 * Pure: items in, ordered items out, `now` supplied by the caller from the injected clock.
 *
 * INVARIANT 7 GOVERNS THIS FILE. A review is never blocked, gated, rationed, or made contingent on
 * anything. There is no cap here, no daily review limit, no "you have too many" cutoff, and no
 * parameter that could become one. `queueDepth` can be 400 and this module will hand back 400 —
 * the honest number, however unwelcome. Everything that *shortens a session* is the round timer's
 * job, and the timer running out is not a queue cut: the unreached items stay due.
 */

import type { ItemRecord } from '../store/schema';
import type { ModeId, Tier } from '../state/gamestate';
import { isDue, retrievability } from './fsrs';

/** States that represent something already introduced. `new` is intake (T23), not review. */
const REVIEWABLE_STATES = new Set(['learning', 'review', 'relearning']);

export function isReviewable(item: ItemRecord): boolean {
  return REVIEWABLE_STATES.has(item.memory.state);
}

/**
 * Every item that is due right now, most at-risk first.
 *
 * Never truncated and never sampled. Callers that want fewer items take a prefix themselves, and
 * the round builder does exactly that only because a 90-second run is short — not because the
 * queue decided anything.
 */
export function dueQueue(items: readonly ItemRecord[], now: number): ItemRecord[] {
  return items.filter((item) => isReviewable(item) && isDue(item.memory, now)).sort(byRisk(now));
}

/**
 * Ordering: lowest retrievability first.
 *
 * "Most overdue" and "most at risk" are not the same thing — an item three days late with a
 * two-year interval is in less danger than one a day late with a three-day interval. Ordering by
 * the forgetting curve puts the items actually about to be lost first, which is what the ordering
 * is for.
 *
 * Ties break on `dueAt` and then on `id`, so the order is total and reproducible. A run replayed
 * from the same seed must produce the same sequence (DoD-14), and an unstable sort on equal keys
 * would break that without ever failing a scheduler test.
 */
function byRisk(now: number): (a: ItemRecord, b: ItemRecord) => number {
  return (a, b) => {
    const ra = retrievability(a.memory, now);
    const rb = retrievability(b.memory, now);
    if (ra !== rb) return ra - rb;
    if (a.memory.dueAt !== b.memory.dueAt) return a.memory.dueAt - b.memory.dueAt;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  };
}

/** How many reviews are waiting. Reported as-is on the entry card and in telemetry. */
export function queueDepth(queue: readonly ItemRecord[]): number {
  return queue.length;
}

// ---------------------------------------------------------------------------------------------
// Time estimation
// ---------------------------------------------------------------------------------------------

/**
 * Seconds per item, by tier, including the feedback card.
 *
 * These are priors, not measurements, and they exist for exactly one decision: whether the queue
 * has grown past 15 minutes and new-item intake should suspend (invariant 6). They are replaced by
 * the learner's own measured medians as soon as there are any — see `estimatedMinutes`.
 *
 * Shaped by the usage scenarios rather than by guesswork about difficulty: tier 5 free-typed
 * sentences are slow because they are typed on a phone with one thumb; tier 1 is short because a
 * spoken vowel is one breath.
 */
export const DEFAULT_TIER_SECONDS: Readonly<Record<Tier, number>> = Object.freeze({
  1: 8,
  2: 11,
  3: 14,
  4: 14,
  5: 26,
  6: 13,
});

/** Measured medians, when the tracker has enough data to have any. Missing keys fall back. */
export type SecondsPerItem = Partial<Record<Tier, number>>;

export function estimatedSeconds(item: ItemRecord, measured: SecondsPerItem = {}): number {
  return measured[item.tier] ?? DEFAULT_TIER_SECONDS[item.tier];
}

/**
 * Estimated minutes to work the whole queue.
 *
 * Deliberately not rounded: the 15-minute suppression threshold is a comparison, and rounding to
 * whole minutes here would make the boundary jump in 30-second steps.
 */
export function estimatedMinutes(
  queue: readonly ItemRecord[],
  measured: SecondsPerItem = {},
): number {
  let seconds = 0;
  for (const item of queue) seconds += estimatedSeconds(item, measured);
  return seconds / 60;
}

// ---------------------------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------------------------

export interface QueueSummary {
  depth: number;
  estimatedMinutes: number;
  /** Depth by tier, for the tracker drilldown. Never rendered as a per-day figure. */
  byTier: Record<Tier, number>;
  /** Items whose retrievability has already fallen below the 0.9 target. */
  atRisk: number;
}

export function summarise(
  queue: readonly ItemRecord[],
  now: number,
  measured: SecondsPerItem = {},
): QueueSummary {
  const byTier: Record<Tier, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  let atRisk = 0;
  for (const item of queue) {
    byTier[item.tier] += 1;
    if (retrievability(item.memory, now) < 0.9) atRisk += 1;
  }
  return {
    depth: queue.length,
    estimatedMinutes: estimatedMinutes(queue, measured),
    byTier,
    atRisk,
  };
}

/**
 * The queue restricted to tiers the learner has unlocked, plus every tier already in circulation.
 *
 * Re-locking a tier (T24) must never remove items from the review queue — invariant 7 forbids
 * gating a review on anything, including on the gate that decides what is *introduced*. An item
 * that is due is due, whatever has happened to its tier since.
 */
export function playableQueue(
  queue: readonly ItemRecord[],
  options: { speechAvailable: boolean },
): ItemRecord[] {
  return queue.filter((item) => {
    // Tier 1 is speech-only and is hidden entirely where the mic is unavailable (SPEC §5). This is
    // the one exclusion, and it is a capability fact, not a gate: the item cannot be answered at
    // all without a microphone.
    if (item.tier === 1 && !options.speechAvailable) return false;
    return true;
  });
}

/**
 * Modes an item can actually be drawn in right now.
 *
 * Two exclusions, and they are different kinds of thing.
 *
 *  1. **Speech, with no microphone** — a capability fact. The card cannot be answered at all.
 *  2. **Free-typed `sentence`, on an item's first exposure** — a pedagogical one, and the ladder
 *     SPEC §5 sets for tier 5: *tiles → free-typed*. A phrase the learner has never seen cannot be
 *     free-typed; asking is not difficulty, it is a guaranteed failure that costs the item an FSRS
 *     lapse it did not earn and teaches nothing on the way. Withheld only while an alternative
 *     exists, so this can never empty the list: an item whose sole mode is `sentence` is still
 *     asked, because a review is never withheld (invariant 7).
 */
export function playableModes(
  item: ItemRecord,
  options: { speechAvailable: boolean },
): ModeId[] {
  const available = item.modes.filter((mode) => mode !== 'speech' || options.speechAvailable);
  if (item.memory.state !== 'new') return available;
  const scaffolded = available.filter((mode) => mode !== 'sentence');
  return scaffolded.length > 0 ? scaffolded : available;
}
